import { Icon, Label } from "@indigo-components";
import { hmsToSeconds } from "@indigo-components/utils/hmsFormat";
import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { VideoPreviewAnchorRect } from "~/atoms/videoPreview";
import { useServices } from "~/hooks/useServices";
import { getSignedPreviewUrl } from "~/services/videoPreviewCache";

export interface FloatingVideoPlayerProps {
  open: boolean;
  onClose: () => void;
  /**
   * The uploaded sub-rosa file to preview. When the panel opens, we
   * resolve its compressed `Document.preview` key (falling back to the
   * original) and sign a download URL. `null` keeps the panel closed.
   */
  file: { id: string; key: string; fileName: string } | null;
  /**
   * Optional HH:MM:SS string to seek to on open. Ignored when unparseable
   * or beyond the video's duration — playback then starts from 0.
   */
  startTime?: string | null;
  /**
   * Bounding rect of the element that triggered the player. Used to
   * position the panel to the right of it on open so the user can see
   * both side-by-side. Omit to center on screen.
   */
  anchorRect?: VideoPreviewAnchorRect;
}

const DEFAULT_WIDTH = 480;
const DEFAULT_HEIGHT = 320;
const GAP = 16;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 200;

/**
 * Pick an initial position for the panel. With an anchor, place the
 * player just to the right of it (e.g. next to the modal or timeline
 * item). Falls back to viewport right edge if that would overflow.
 * No anchor → center on screen.
 */
const computeInitialPosition = (
  anchor: FloatingVideoPlayerProps["anchorRect"] | undefined,
  width: number,
  height: number,
): { top: number; left: number } => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!anchor) {
    return {
      top: Math.max(GAP, (vh - height) / 2),
      left: Math.max(GAP, (vw - width) / 2),
    };
  }
  let left = anchor.right + GAP;
  if (left + width + GAP > vw) left = Math.max(GAP, vw - width - GAP);
  let top = anchor.top;
  if (top + height + GAP > vh) top = Math.max(GAP, vh - height - GAP);
  return { top, left };
};

/**
 * Non-blocking floating video player for sub-rosa entries. Draggable
 * via the header, resizable via the bottom-right corner (native CSS
 * resize). Plays the compressed preview mp4 written by the video-
 * summarization batch job (PR #6623), falling back to the original
 * upload when no preview is attached yet.
 */
export const FloatingVideoPlayer: FC<FloatingVideoPlayerProps> = ({
  open,
  onClose,
  file,
  startTime,
  anchorRect,
}) => {
  const { s3 } = useServices();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const dragState = useRef<{
    startX: number;
    startY: number;
    origTop: number;
    origLeft: number;
    cleanup: () => void;
  } | null>(null);

  // Reset position when the panel is freshly opened or anchored at a
  // different timeline item.
  useLayoutEffect(() => {
    if (!open) return;
    setPos(computeInitialPosition(anchorRect, DEFAULT_WIDTH, DEFAULT_HEIGHT));
  }, [open, anchorRect]);

  useEffect(() => {
    if (!open || !file || !s3) {
      setSignedUrl(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSignedPreviewUrl(s3, file)
      .then((url) => {
        if (!cancelled) {
          setSignedUrl(url);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, file, s3]);

  // Ensure any in-flight drag listeners are torn down if the panel
  // unmounts mid-drag (e.g. parent closes via onClose).
  useEffect(() => {
    return () => {
      dragState.current?.cleanup();
    };
  }, []);

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video || !startTime) return;
    const seconds = hmsToSeconds(startTime);
    if (seconds === null || !Number.isFinite(video.duration)) return;
    if (seconds <= 0 || seconds >= video.duration) return;
    video.currentTime = seconds;
  };

  const handleDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    // Let buttons in the header (e.g. the close icon) handle their own clicks.
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    const handleMove = (ev: MouseEvent) => {
      if (!dragState.current) return;
      const rawTop =
        dragState.current.origTop + (ev.clientY - dragState.current.startY);
      const rawLeft =
        dragState.current.origLeft + (ev.clientX - dragState.current.startX);
      // Clamp so the panel always keeps at least MIN_* visible — a
      // user dragging aggressively past an edge can otherwise lose the
      // panel off-screen with no way to recover it.
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setPos({
        top: Math.max(GAP, Math.min(rawTop, vh - MIN_HEIGHT - GAP)),
        left: Math.max(GAP, Math.min(rawLeft, vw - MIN_WIDTH - GAP)),
      });
    };
    const cleanup = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      dragState.current = null;
      setIsDragging(false);
    };
    const handleUp = () => cleanup();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origTop: pos.top,
      origLeft: pos.left,
      cleanup,
    };
    setIsDragging(true);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-label="Video preview"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
        minWidth: MIN_WIDTH,
        minHeight: MIN_HEIGHT,
        maxWidth: "90vw",
        maxHeight: "90vh",
        resize: "both",
        overflow: "hidden",
        background: "white",
        borderRadius: 8,
        boxShadow: "0 10px 32px rgba(0, 0, 0, 0.25)",
        // Above page content; below the toast/notification layer (2300).
        zIndex: 1500,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        onMouseDown={handleDragStart}
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          padding: "8px 12px",
          borderBottom: "1px solid var(--indigo-border-default)",
          background: "var(--indigo-canvas-shade)",
          flexShrink: 0,
        }}
        className="flex items-center justify-between gap-2"
      >
        <div className="flex flex-col gap-0.5 pr-2 overflow-hidden min-w-0">
          <Label size="md" weight="semibold">
            Video preview
          </Label>
          {file?.fileName && (
            <div className="text-xs text-indigo-text-tertiary font-normal truncate font-mono">
              {file.fileName}
            </div>
          )}
        </div>
        <button
          type="button"
          aria-label="Close video preview"
          onClick={onClose}
          className="p-1 rounded hover:bg-indigo-gray-100 flex items-center justify-center shrink-0"
        >
          <Icon name="close" sizeX={14} sizeY={14} />
        </button>
      </div>

      <div className="flex-1 min-h-0 bg-black flex items-center justify-center text-indigo-text-on-accent">
        {loading && (
          <div className="text-sm text-indigo-text-disabled">
            Loading video…
          </div>
        )}
        {error && (
          <div className="text-sm text-rose-400 px-4 text-center">
            Failed to load video: {error.message}
          </div>
        )}
        {!loading && !error && signedUrl && (
          // `muted` keeps `autoPlay` permitted under browser autoplay
          // policies — sub-rosa clips are surveillance footage with
          // ambient audio, which most browsers block from autoplaying.
          // The user can unmute via the native <video> controls.
          <video
            ref={videoRef}
            controls
            autoPlay
            muted
            src={signedUrl}
            onLoadedMetadata={handleLoadedMetadata}
            className="w-full h-full object-contain"
          />
        )}
      </div>
    </div>,
    document.body,
  );
};

export default FloatingVideoPlayer;
